/**
 * Admin → Plans (subscription packages a reseller offers to its
 * customers). Backed by /v1/admin/plans/*; same gate as the rest of
 * /v1/admin/* (RequireReseller). Customer-tenant users get 403.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type PlanColor = 'neutral' | 'volt' | 'ink';
/** plans.voice_tier_max — per-plan ceiling on which voice_catalog.tier
 *  the plan's customers can see in the AI Agent profile editor. */
export type PlanVoiceTier = 'standard' | 'premium';

export interface Plan {
  id: number;
  name: string;
  tagline?: string;
  monthly_fee: number;
  /** null = "negotiated" — Enterprise tier OR voice-only plan (no SMS). */
  included_sms?: number | null;
  overage_per_sms: number;
  /** null = plan doesn't bundle voice (customer pays per-minute via rate card). */
  included_voice_minutes?: number | null;
  features: string[];
  is_popular: boolean;
  color: PlanColor;
  /** Plan-specific rate card. Null = use the reseller's default. */
  rate_card_id?: number | null;
  /** voice_tier_max ceiling — populated by the platform-admin enrichment
   *  on /v1/admin/plans. May be undefined for older clients hitting an
   *  API that pre-dates Sprint 1 of the voice catalog rollout; treat
   *  missing as 'standard' (matches the DB default in migration 69). */
  voice_tier_max?: PlanVoiceTier;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlansResponse {
  items: Plan[];
  total: number;
}

export interface PlanInput {
  name: string;
  tagline?: string;
  monthly_fee: number;
  included_sms?: number | null;
  overage_per_sms: number;
  included_voice_minutes?: number | null;
  features: string[];
  is_popular: boolean;
  color: PlanColor;
  rate_card_id?: number | null;
  is_active: boolean;
}

export interface PlanSubscriber {
  plan_id: number;
  name: string;
  color: PlanColor;
  subscribers: number;
  mrr: number;
}

export interface PlanSubscribersResponse {
  items: PlanSubscriber[];
}

const PLANS_KEY = ['admin', 'plans'] as const;
const PLAN_SUBSCRIBERS_KEY = ['admin', 'plans', 'subscribers'] as const;

export function usePlans() {
  return useQuery({
    queryKey: PLANS_KEY,
    queryFn: () => api.get<PlansResponse>('/v1/admin/plans'),
    staleTime: 30_000,
  });
}

export function usePlanSubscribers() {
  return useQuery({
    queryKey: PLAN_SUBSCRIBERS_KEY,
    queryFn: () => api.get<PlanSubscribersResponse>('/v1/admin/plans/subscribers'),
    staleTime: 30_000,
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PlanInput) => api.post<Plan>('/v1/admin/plans', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANS_KEY });
      void qc.invalidateQueries({ queryKey: PLAN_SUBSCRIBERS_KEY });
    },
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: PlanInput }) =>
      api.put<Plan>(`/v1/admin/plans/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANS_KEY });
      void qc.invalidateQueries({ queryKey: PLAN_SUBSCRIBERS_KEY });
    },
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/admin/plans/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANS_KEY });
      void qc.invalidateQueries({ queryKey: PLAN_SUBSCRIBERS_KEY });
    },
  });
}

/** Flip plans.voice_tier_max between 'standard' and 'premium'. Backed
 *  by POST /v1/admin/plans/{id}/voice-tier — platform-admin only. The
 *  customer-facing voice catalog endpoint reads this column to decide
 *  which voices to expose, so a successful flip changes what the
 *  tenant's AI Agent editor sees on next paint.
 *
 *  The plans list cache is invalidated on success so the dropdown in
 *  the editor reflects the new tier without a manual refetch. */
export function useSetPlanVoiceTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tier }: { id: number; tier: PlanVoiceTier }) =>
      api.post<void>(`/v1/admin/plans/${id}/voice-tier`, { voice_tier_max: tier }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLANS_KEY });
    },
  });
}
